import { AxiosResponse } from 'axios';
import { sortCategoriesByIndex } from 'utils/service-order';
import { pageLoading } from 'components/PageLoading';
import BookingV3Manager from 'features/Booking/v3/controller';
import { sumBy } from 'lodash';
import moment from 'moment';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { ISagaFunc } from 'services/actionConfigs';
import shopActions from 'services/shop/actions';
import shopApis from 'services/shop/apis';
import { RootState } from 'store';
import { ICategoryResItem, IServiceResItem, IStaffResItem } from 'types/response';
import { IShopInfoResData } from 'types/shop';
import { IStaffUI } from 'types/staff';
import { formatPhone } from 'utils/formatPhone';
import storage from 'utils/sessionStorage';
import { ANYONE_STAFF } from '../BookingPage/components/BookingSteps/helper';
import { BOOKING_STEP, bookingSignal } from '../v2/pages/BookingPage/signal';
import { successData } from '../v2/pages/SuccessPage/signal';
import actions from './actions';
import apis from './apis';
import { IAppointmentDetailResData } from './types/appointment';
import { ICustomerDetailByIdResData } from './types/customer';
import { ITransactionDetailResData } from './types/transaction';

const getAptDetailInfo: ISagaFunc<string> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IAppointmentDetailResData }> = yield call(apis.getAppointmentDetail, payload);
    const aptDetail = res?.data?.payload;
    if (aptDetail) {
      yield put(actions.detail.success(aptDetail));
    }
  } catch (error) { }
};

const getTransactionDetail: ISagaFunc<string> = function* ({ payload }) {
  yield delay(200);
  try {
    const res: AxiosResponse<{ payload: ITransactionDetailResData }> = yield call(apis.transactionDetail, payload);
    const aptDetail = res?.data?.payload;
    if (aptDetail) {
      yield put(actions.getTransactionDetail.success(aptDetail));
      if (aptDetail.status) {
        const staffs: IStaffUI[] = yield select((root: RootState) => root.shop.staffs);
        const startTime = moment(aptDetail.appointment.strStartDate, 'MM-DD-YYYY hh:mm A');
        const duration = sumBy(aptDetail.appointment.services, o => o.duration);
        const endTime = startTime.clone().add(duration, 'minute');

        successData.value = {
          startTime, endTime,
          QRCode: aptDetail.appointment.qrCode,
          services: aptDetail.appointment.services.map(o => ({
            id: o.id.toString() || '',
            name: o.serviceName || '',
            price: o.price || 0,
            image: '',
            cateId: '',
            duration: o.duration,
          })),
          staff: staffs.find(o => o.id.toString() === aptDetail.appointment?.staffId?.toString()) || ANYONE_STAFF,
          note: aptDetail.appointment.note || '',
          coupon: aptDetail.appointment.coupon,
          customer: null,
        };
      }
    } else {
      yield put(actions.getTransactionDetail.fail({}));
    }
  } catch (error) {
    yield put(actions.getTransactionDetail.fail({}));
  }
};

const initCustomerId = function* ({ payload }: { payload: string }) {
  pageLoading.value = true;
  yield delay(200);
  try {
    const res: AxiosResponse<{ payload: ICustomerDetailByIdResData }> = yield call(apis.getCustomerDetailById, payload);
    if (res.data.payload) {
      const customerInfo = res.data.payload;
      bookingSignal.customerInfo.value = {
        id: customerInfo?.customerId?.toString() || '',
        name: customerInfo.customerName,
        phone: formatPhone(customerInfo.customerPhone),
      };
      bookingSignal.onChooseTab(BOOKING_STEP.SERVICE_STEP);
    }
  } catch (error) {
  } finally {
    pageLoading.value = false;
  }
};

const getCategories = function* ({ payload }: { payload?: number }) {
  try {
    const ser: AxiosResponse<{ payload: IServiceResItem[] }> = yield call(shopApis.getServicesByStaffId, payload);
    const servicesRes: IServiceResItem[] = ser?.data?.payload;
    if (servicesRes) {
      yield put(actions.services.success(servicesRes));
    }
  } catch (error) { }
};

const bookingv3GetAvailableTimeSlotByStaffAndDate: ISagaFunc<{ staffId: string, currentDate: string, duration: number }> = function* ({ payload }) {
  try {
    // Anyone (empty staffId): the V2 endpoint only handles a specific staff and returns no slots,
    // so fall back to the legacy endpoint, which returns the shop's open hours.
    const res: AxiosResponse<{ payload: { timeSlots: string[] } }> = payload.staffId
      ? yield call(shopApis.getTimeSlotV2, payload.currentDate, payload.staffId, payload.duration)
      : yield call(shopApis.getTimeSlot, payload.currentDate, '');
    if (res.data.payload.timeSlots?.length) {
      yield put(actions.bookingv3GetAvailableTimeSlotByStaffAndDate.success(res.data.payload.timeSlots));
    } else {
      yield put(actions.bookingv3GetAvailableTimeSlotByStaffAndDate.fail({}));
    }
  } catch (error) {
    yield put(actions.bookingv3GetAvailableTimeSlotByStaffAndDate.fail({}));
  } finally {
    yield delay(200);
    document.getElementById('step-2-tab')?.scrollIntoView({ behavior: 'smooth' });
  }
};

const bookingV3GetCategories = function* () {
  try {
    const res: AxiosResponse<{ payload: ICategoryResItem[] }> = yield call(shopApis.getCategories);
    const categoriesRes = res?.data?.payload;
    if (categoriesRes?.length) {
      yield put(actions.bookingV3GetCategories.success(sortCategoriesByIndex(categoriesRes)));
    } else {
      yield put(actions.bookingV3GetCategories.fail({}));
    }
  } catch (error) {
    yield put(actions.bookingV3GetCategories.fail({}));
  }
};

const bookingV3GetStaffsAvailableByDate = function* ({ payload }: { payload: string }) {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(shopApis.getStaffsAvailableByCurrentDate, payload);
    const staffsRes = res?.data?.payload;
    if (staffsRes?.length) {
      yield put(actions.bookingV3GetStaffsAvailableByDate.success(staffsRes));
    } else {
      yield put(actions.bookingV3GetStaffsAvailableByDate.fail({}));
    }
  } catch (error) {
    yield put(actions.bookingV3GetStaffsAvailableByDate.fail({}));
  }
};

const getBookingV3Init = function* ({ payload }: { payload: string }) {
  yield storage.shopId.set(payload);
  yield delay(200);
  pageLoading.value = true;
  try {
    const res: AxiosResponse<{ payload: IShopInfoResData }> = yield call(shopApis.getShopDetail, payload);
    const shopData = res.data.payload;
    if (shopData) {
      yield put(shopActions.shopInfo.success(shopData));
    } else {
      window.location.href = '/404';
      return;
    }
  } catch (error) {
    window.location.href = '/404';
    return;
  }
  finally {
    pageLoading.value = false;
  }

  BookingV3Manager.resetFields();

  yield put(shopActions.staff.fetch());
  yield put(shopActions.settings.fetch());
  yield put(actions.bookingV3GetCategories.fetch(payload));
  yield put(actions.bookingV3GetStaffsAvailableByDate.fetch(moment().format('YYYY-MM-DD')));

  yield put(actions.bookingV3Init.success());
};

export default function* aptSagas() {
  yield takeLatest(actions.detail.fetch, getAptDetailInfo);
  yield takeLatest(actions.getTransactionDetail.fetch, getTransactionDetail);
  yield takeLatest(actions.initCustomerId, initCustomerId);
  yield takeLatest(actions.services.fetch, getCategories);
  yield takeLatest(actions.bookingV3Init.fetch, getBookingV3Init);
  yield takeLatest(actions.bookingV3GetStaffsAvailableByDate.fetch, bookingV3GetStaffsAvailableByDate);
  yield takeLatest(actions.bookingV3GetCategories.fetch, bookingV3GetCategories);
  yield takeLatest(actions.bookingv3GetAvailableTimeSlotByStaffAndDate.fetch, bookingv3GetAvailableTimeSlotByStaffAndDate);
}